import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import { styled, type Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { Link as RouterLink } from "react-router";

/**
 * The pieces a dashboard card is built from. They live here rather than in the page because the
 * dashboard draws several cards side by side: anything defined privately in one of them drifts
 * away from its neighbour, and two cards that are almost the same shape read worse than two that
 * are either the same or plainly different.
 */
export const DashboardCard = styled(Card)(({ theme }) => ({
  minHeight: 320,
  height: "100%",
  overflow: "hidden",
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[3],
}));

/**
 * A card that holds its own content rather than a full-bleed image.
 *
 * The divider outline belongs here rather than on the card above, because what it is for is telling
 * a pale panel from the pale page behind it. The one card that is an image edge to edge has an edge
 * of its own, and a hairline drawn over it only crops the picture.
 */
export const DashboardPanelCard = styled(DashboardCard)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  // A panel is as tall as what is in it. The 320px floor above is there for the card that is a
  // photograph, which becomes a strip without one; on a panel holding a single row it is 160px of
  // white under the row, and a card with one thing in it should look like a card with one thing in
  // it rather than like a card that failed to load the rest.
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(2.5),
  background: `linear-gradient(145deg, ${theme.palette.background.paper} 50%, ${theme.palette.action.hover})`,
}));

export const DashboardCardEyebrow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.75),
}));

export const DashboardCardHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1.5),
  "& > .MuiBox-root": {
    flex: 1,
    minWidth: 0,
  },
  "& > .MuiButton-root": {
    // Keep short actions at their natural width; narrower cards can still wrap the label.
    flexShrink: 0,
    width: "max-content",
    maxWidth: "50%",
    minWidth: 0,
    textAlign: "right",
  },
}));

const TitleLink = styled(RouterLink)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  color: "inherit",
  textDecoration: "none",
  borderRadius: theme.shape.borderRadius,
  "& .DashboardCardTitleArrow": {
    color: theme.palette.primary.main,
    transition: theme.transitions.create("transform"),
  },
  "&:hover": {
    textDecoration: "underline",
    "& .DashboardCardTitleArrow": { transform: "translateX(2px)" },
  },
  "&:focus-visible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
}));

/**
 * A card's title, and the way to the page the card summarizes (#6584).
 *
 * The title is the link, not a "View all" in the corner: one large target, named for both what the
 * card says and where it goes ("Conversations: 16 unread messages"). The eyebrow above it stays a
 * label. Drawn as the heading it is, with an arrow and an underline on hover saying it can be
 * followed, rather than as a link colored like body text beside the card's other links.
 *
 * A card with no single page behind it keeps a plain `h2`.
 */
export function DashboardCardTitle(
  props: Readonly<{ to: string; label: string; children: string }>,
) {
  return (
    <Typography variant="h5" component="h2">
      <TitleLink aria-label={`${props.label}: ${props.children}`} to={props.to}>
        {props.children}
        <ArrowForwardRoundedIcon aria-hidden className="DashboardCardTitleArrow" fontSize="small" />
      </TitleLink>
    </Typography>
  );
}

/** Loading, error and empty all say one short thing in the middle of the card. */
export const DashboardCardState = styled(Box)(({ theme }) => ({
  display: "flex",
  flex: 1,
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1),
  color: theme.palette.text.secondary,
  textAlign: "center",
}));

/**
 * One row shape, shared by every list a dashboard card draws: a fixed-width marker, the text, and
 * whatever trails it. Upcoming events put a date in the marker and the waiting-on-you queue puts a
 * count, but they are the same row and have to stay the same row.
 */
const dashboardRowStyles = (theme: Theme) => ({
  display: "grid",
  gridTemplateColumns: "48px minmax(0, 1fr) auto",
  alignItems: "center",
  gap: theme.spacing(1.25),
  minHeight: 62,
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
} as const);

const dashboardRowLinkStyles = (theme: Theme) => ({
  ...dashboardRowStyles(theme),
  width: "100%",
  color: theme.palette.text.primary,
  textDecoration: "none",
  textAlign: "left",
  transition: theme.transitions.create(["background-color", "border-color", "transform"]),
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.primary.main,
    transform: "translateY(-1px)",
  },
} as const);

export const DashboardRow = styled("div")(({ theme }) => dashboardRowStyles(theme));
export const DashboardRowLink = styled(RouterLink)(({ theme }) => dashboardRowLinkStyles(theme));
export const DashboardExternalRowLink = styled("a")(({ theme }) => dashboardRowLinkStyles(theme));

/**
 * How loudly a row reads.
 *
 * `action` is the solid primary block, and it is for one thing: a row that is the viewer's move,
 * something the platform is holding until they answer. `quiet` is for everything that is a record
 * rather than a demand — work already under way, an event on a date, an announcement of something
 * that has already happened.
 *
 * Every row on the dashboard used to be `action`, so a read-through log of what happened last week
 * shouted exactly as loudly as the queue beside it and nothing on the page stood out at all
 * (#5123). The distinction is only worth having while it stays scarce, which is why a card
 * reaching for `action` should be able to say whose move the row is.
 */
export type DashboardRowEmphasis = "action" | "quiet";

/** The 48px block at the head of a row. */
export const DashboardRowMarker = styled(Box, {
  shouldForwardProp: (prop) => prop !== "emphasis",
})<{ emphasis?: DashboardRowEmphasis }>(({ emphasis = "action", theme }) => ({
  display: "flex",
  minHeight: 44,
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: theme.shape.borderRadius,
  ...(emphasis === "quiet"
    ? {
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.text.secondary,
      }
    : {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
      }),
}));

export const DashboardRowText = styled(Box)({ minWidth: 0 });

/**
 * The dashboard's composition, in one rule: every card is one cell of a grid whose columns are all
 * the same width, and the grid fits as many of them across as the pane allows.
 *
 * The page used to be a two-column split tuned for exactly three cards — a left band of stacked
 * tiles beside the calendar — so a fourth card unbalanced the columns and the whole thing had to be
 * re-measured. Nothing here is tuned for a count: `auto-fit` adds and drops columns on its own, so
 * a card added or removed changes how many rows there are and nothing else. That is the property
 * this grid exists to have, and the reason to be suspicious of any card that wants its own width.
 *
 * 320px is the floor because `DateCalendar` is a 320px day grid that does not shrink, and the
 * calendar is a card like the others.
 */
export const DashboardCardGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
  gap: theme.spacing(2),
  alignItems: "stretch",
}));

/**
 * A card's list of rows, and the overflow control under it.
 *
 * The rows sit at the top and the "+N more" button is pushed to the bottom edge, so cards holding
 * different numbers of rows still line their controls up with each other.
 */
export const DashboardRowList = styled(Box)(({ theme }) => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  gap: theme.spacing(1),
  "& > :last-child:not(:only-child)": {
    marginTop: "auto",
  },
}));

/**
 * Text that has to fit a row without the row growing: two lines, then an ellipsis.
 *
 * Notification prose is written for the bell's flyout and is a whole sentence, not a label, so the
 * alternative to clamping is either a row that grows to fit one long sentence or `noWrap`, which
 * cuts most of them off after four words.
 */
export const DashboardRowProse = styled("span")({
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 2,
  overflow: "hidden",
});
